import { canOpenResource } from './access';
import type { BusinessService } from './service';
import type { Entity, Kind } from './types';

export const workspaceResources: Partial<Record<Kind, string>> = {
  recording: 'recording',
  clue: 'clue',
  case: 'case',
  plan: 'plan',
  followUp: 'service',
  batch: 'collection',
  device: 'device',
  knowledge: 'knowledge',
};
export function recommendation(row: Entity, now: string) {
  if (row.kind === 'recording' && row.fields?.linkStatus === 'pending')
    return {
      title: '录音归属待确认',
      reason: '录音尚未完成业务关联，需核对发生时的领用与课次记录。',
      action: '核实录音归属',
      level: 2,
    };
  if (row.kind === 'batch' && row.status === 'failed')
    return {
      title: '采集异常待恢复',
      reason:
        row.note ||
        '采集批次失败，需核对文件阶段并重试；传输完成不等于后台确认。',
      action: '诊断采集异常',
      level: 0,
    };
  if (row.kind === 'case' && row.status !== 'closed')
    return {
      title:
        row.status === 'pendingAcceptance' ? '事项等待验收' : '事项处理待闭环',
      reason:
        row.status === 'pendingAcceptance'
          ? '处理结果已提交，需核验措施和任务完成情况。'
          : '事项尚未关闭，请明确责任人、处理措施与验收依据。',
      action: '推进事项闭环',
      level: row.dueAt && Date.parse(row.dueAt) < Date.parse(now) ? 0 : 1,
    };
  if (
    row.kind === 'followUp' &&
    ![
      'completed',
      'closed',
      'cancelled',
      'draft',
      'candidate',
      'excluded',
    ].includes(row.status)
  )
    return {
      title: '服务承诺待跟进',
      reason:
        row.dueAt && Date.parse(row.dueAt) < Date.parse(now)
          ? '约定期限已过且尚无完成记录，请核实履行情况与延期原因。'
          : '服务跟进尚未完成，请按约定期限核实结果。',
      action: '跟进服务承诺',
      level: row.dueAt && Date.parse(row.dueAt) < Date.parse(now) ? 0 : 1,
    };
  if (
    row.kind === 'plan' &&
    !['completed', 'closed', 'draft', 'cancelled'].includes(row.status)
  )
    return {
      title: '教学实践待复盘',
      reason:
        '计划尚未验收，需补充课堂实践与反馈，避免以建立计划替代完成改进。',
      action: '复盘教学实践',
      level: 2,
    };
  return undefined;
}
export function workspaceVisible(
  service: Pick<BusinessService, 'state' | 'get'>,
  row: Entity,
  visited = new Set<string>(),
): boolean {
  if (!service.state || !service.get(row.uuid) || visited.has(row.uuid))
    return false;
  visited.add(row.uuid);
  const resource = workspaceResources[row.kind];
  if (resource && !canOpenResource(resource, service.state)) return false;
  const publishedCase = row.kind === 'knowledge' && row.status === 'published';
  if (row.evidence && !publishedCase) {
    const source = service.get(row.evidence.recordingUuid);
    if (!source || !canOpenResource('recording', service.state)) return false;
  }
  if (
    row.sourceUuid &&
    [
      'task',
      'message',
      'audit',
      'job',
      'clue',
      'case',
      'followUp',
      'feedback',
      'correction',
    ].includes(row.kind)
  ) {
    const source = service.get(row.sourceUuid);
    if (!source || !workspaceVisible(service, source, visited)) return false;
  }
  return true;
}
export function buildWorkspace(service: BusinessService) {
  const state = service.state;
  if (!state) throw new Error('业务数据尚未加载');
  const read = (kind: Kind) => {
    const resource = workspaceResources[kind];
    if (resource && !canOpenResource(resource, state)) return [];
    return service.list(kind).filter((e) => workspaceVisible(service, e));
  };
  const recordings = read('recording');
  const clues = read('clue');
  const cases = read('case');
  const follows = read('followUp');
  const plans = read('plan');
  const batches = read('batch');
  const tasks = service
    .list('task')
    .filter((e) => workspaceVisible(service, e));
  const activeTasks = tasks.filter(
    (e) => !['completed', 'cancelled', 'closed'].includes(e.status),
  );
  const concerns = [...batches, ...cases, ...follows, ...plans, ...recordings]
    .map((row) => ({
      row,
      advice: recommendation(row, state.now),
      task: activeTasks.find((t) => t.sourceUuid === row.uuid),
    }))
    .filter(
      (x): x is typeof x & { advice: NonNullable<typeof x.advice> } =>
        !!x.advice,
    )
    .sort(
      (a, b) =>
        a.advice.level - b.advice.level ||
        (a.row.dueAt || '9999').localeCompare(b.row.dueAt || '9999') ||
        a.row.uuid.localeCompare(b.row.uuid),
    );
  const closed = cases.filter((e) => e.status === 'closed');
  const reviewed = clues.filter((e) =>
    ['confirmed', 'excluded', 'needsInfo'].includes(e.status),
  );
  const overdue = follows.filter(
    (e) =>
      ![
        'completed',
        'closed',
        'cancelled',
        'draft',
        'candidate',
        'excluded',
      ].includes(e.status) &&
      e.dueAt &&
      Date.parse(e.dueAt) < Date.parse(state.now),
  );
  const metrics = [
    {
      key: 'recordings',
      title: '录音归集',
      value: String(recordings.length),
      unit: '条',
      note: '当前授权范围全部录音',
      rows: recordings,
    },
    {
      key: 'reviews',
      title: '已审阅线索',
      value: clues.length ? `${reviewed.length} / ${clues.length}` : '—',
      unit: '条',
      note: '确认、排除、信息不足均计为已审阅',
      rows: reviewed,
    },
    {
      key: 'closed',
      title: '事项关闭',
      value: cases.length ? `${closed.length} / ${cases.length}` : '—',
      unit: '项',
      note: `${cases.length - closed.length}项尚未关闭`,
      rows: closed,
    },
    {
      key: 'overdue',
      title: '逾期服务跟进',
      value: String(overdue.length),
      unit: '项',
      note: '已过约定期限且尚无完成记录',
      rows: overdue,
    },
  ];
  if (!canOpenResource('recording', state)) {
    const definitions: {
      kind: Kind;
      title: string;
      note: string;
      filter?: (e: Entity) => boolean;
    }[] =
      state.context.role === 'systemAdmin'
        ? [
            { kind: 'rule', title: '业务规则', note: '当前范围可访问规则' },
            {
              kind: 'policy',
              title: '业务权限策略',
              note: '原型业务策略，不覆盖真实平台权限',
            },
            {
              kind: 'integration',
              title: '同步配置',
              note: '模拟数据同步配置',
            },
            {
              kind: 'modelConfig',
              title: '处理配置',
              note: '模拟处理能力配置',
            },
          ]
        : ['deviceManager', 'support'].includes(state.context.role)
          ? [
              { kind: 'device', title: '所辖设备', note: '当前范围可访问资产' },
              {
                kind: 'device',
                title: '在库可领用',
                note: '尚未绑定人员的在库设备',
                filter: (e) => e.status === 'stock',
              },
              {
                kind: 'batch',
                title: '待恢复采集',
                note: '失败批次，传输与后台确认独立',
                filter: (e) => e.status === 'failed',
              },
              {
                kind: 'repair',
                title: '未关闭维修',
                note: '需要处理或验收的维修记录',
                filter: (e) => !['closed', 'completed'].includes(e.status),
              },
            ]
          : [
              {
                kind: 'task',
                title: '待完成任务',
                note: '当前授权可访问任务',
                filter: (e) => !['completed', 'cancelled'].includes(e.status),
              },
              {
                kind: 'knowledge',
                title: '已发布案例',
                note: '已授权的发布案例',
                filter: (e) => e.status === 'published',
              },
              {
                kind: 'message',
                title: '未读消息',
                note: '来源可访问的业务消息',
                filter: (e) => e.status === 'unread',
              },
              { kind: 'pilot', title: '试点记录', note: '当前范围的试点记录' },
            ];
    metrics.splice(
      0,
      metrics.length,
      ...definitions.map((d, i) => {
        const rows = read(d.kind).filter((e) => !d.filter || d.filter(e));
        return {
          key: `role-${i}`,
          title: d.title,
          value: String(rows.length),
          unit: '项',
          note: d.note,
          rows,
        };
      }),
    );
  }
  const activities = service
    .list('audit')
    .filter((e) => e.sourceUuid && workspaceVisible(service, e))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 5);
  return {
    state,
    metrics,
    concerns,
    activeTasks: activeTasks.sort((a, b) =>
      (a.dueAt || '9999').localeCompare(b.dueAt || '9999'),
    ),
    activities,
    recordings: recordings.sort((a, b) =>
      (b.startAt || b.createdAt).localeCompare(a.startAt || a.createdAt),
    ),
    outcomes: [
      {
        title: '已验收教学实践',
        value: plans.filter((e) => ['completed', 'closed'].includes(e.status))
          .length,
        rows: plans.filter((e) => ['completed', 'closed'].includes(e.status)),
      },
      {
        title: '已审核发布案例',
        value: read('knowledge').filter((e) => e.status === 'published').length,
        rows: read('knowledge').filter((e) => e.status === 'published'),
      },
    ],
    stages: [
      { title: '录音归集', value: recordings.length },
      { title: '候选线索', value: clues.length },
      {
        title: '人工确认',
        value: clues.filter((e) => e.status === 'confirmed').length,
      },
      { title: '事项关闭', value: closed.length },
    ],
  };
}
